import {
  CAPTURE_STATUS,
  PERSISTENT_KEYS,
  SESSION_KEYS,
  createPersistentFlag,
  createSessionFlag,
  createSessionValue,
  readSourceParam,
  safeSuccessUrl,
  shouldFireExitIntent,
  submitCapture,
  unlockFlagKey,
} from './eg-capture-state.js';
import { closeModalWithExit } from './eg-modal-exit.js';

/**
 * Capture surfaces — DOM wiring only (U10 / R15). All logic lives in
 * eg-capture-state.js (the tested module).
 *
 * Three surfaces, one rule each:
 *   sticky bar   — one site-wide, dismissible, dismissal holds for the session
 *   inline form  — the only email-capture surface; one success state, one failure state
 *   exit-intent  — fallback, once per session, desktop pointers only
 *
 * Every capture surface renders `hidden` and is un-hidden here. With JS off
 * nothing shows — deliberate: a no-JS form would either POST to the proxy and
 * paint raw JSON at the shopper, or fall back to a GET that writes their email
 * into the URL and their history. Showing no form beats both.
 */

const SELECTORS = {
  bar: '[data-eg-sticky-bar]',
  barDismiss: '[data-eg-sticky-bar-dismiss]',
  form: '[data-eg-capture-form]',
  exitIntent: '[data-eg-exit-intent]',
  exitIntentClose: '[data-eg-exit-intent-close]',
};

function log(message, detail) {
  console.info(`[eg-capture] ${message}`, detail ?? '');
}

/**
 * PostHog / analytics dispatch (U22 amendment, 2026-09-02) — same pattern as
 * eg-configurator.js and eg-ladder.js: no declaring element, so a DOM
 * CustomEvent stands in for `data-eg-event`, and eg-analytics.js relays it.
 * Fails silent: a capture surface's whole point is not costing a shopper's
 * email, so analytics can never be the thing that breaks it.
 */
function track(name, params) {
  try {
    document.dispatchEvent(new CustomEvent('eg:track', { detail: { name, params: params || {} } }));
  } catch {
    /* analytics must never break the storefront */
  }
}

const barFlag = createSessionFlag(SESSION_KEYS.BAR_DISMISSED);
const exitIntentFlag = createSessionFlag(SESSION_KEYS.EXIT_INTENT_FIRED);
const quizEnteredFlag = createPersistentFlag(PERSISTENT_KEYS.QUIZ_ENTERED);

/**
 * Capture source (2026-09-22, U3): a `?src=` a shopper arrived with is
 * remembered for the rest of the session, on EVERY page load — not just ones
 * with a capture form — so a landing page carrying the param and the page
 * whose form she eventually submits need not be the same view.
 */
const captureSrc = createSessionValue(SESSION_KEYS.CAPTURE_SRC);
const initialCaptureSrc = readSourceParam(location.search);
if (initialCaptureSrc) captureSrc.set(initialCaptureSrc);

/* ---------------------------------------------------------------- sticky bar */

function initStickyBar(bar) {
  // Dismissed this session, or entered the quiz EVER (Kyle, 2026-07-25):
  // either way the bar stays hidden.
  if (barFlag.isSet() || quizEnteredFlag.isSet()) return;
  bar.hidden = false;

  const dismiss = bar.querySelector(SELECTORS.barDismiss);
  if (!dismiss) return;
  dismiss.addEventListener('click', () => {
    barFlag.set();
    bar.hidden = true;
  });
}

/* Any quiz-launch surface (the bar's link, hero CTAs, CTA bands, …): clicking
   through marks the shopper as having entered the quiz — forever — and drops
   the bar on the spot so a target=_blank launch doesn't leave it behind. */
document.addEventListener('click', (event) => {
  const launch = event.target instanceof Element && event.target.closest('[data-eg-event="quiz-launch"]');
  if (!launch) return;
  quizEnteredFlag.set();
  document.querySelectorAll(SELECTORS.bar).forEach((bar) => {
    bar.hidden = true;
  });
});

/* --------------------------------------------------------------- inline form */

/**
 * Wires one inline capture form. `root` holds three mutually exclusive panels
 * (form / success / error-adjacent retry) so the success state is the SAME
 * markup on every placement — blog, content page, exit-intent modal.
 */
/* ---------------------------------------------------------------- turnstile */

/**
 * Cloudflare Turnstile, loaded once and only by a form that asks for it (a
 * phone form carries data-turnstile-sitekey). Explicit render so the script
 * never scans the page on its own.
 */
const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=egTurnstileReady';
let turnstileLoad = null;

function loadTurnstile() {
  if (turnstileLoad) return turnstileLoad;
  turnstileLoad = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    window.egTurnstileReady = () => resolve(window.turnstile);
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(script);
  });
  return turnstileLoad;
}

/**
 * One form's bot check. `token()` resolves to the current token, waiting up
 * to `waitMs` for a check still in flight, and to '' when there is none (the
 * Worker then refuses, and the form shows its retry line). `reset()` asks for
 * a fresh token: each one is single-use, so every submit spends it.
 */
function createTurnstileCheck(root, siteKey, waitMs = 8000) {
  const mount = root.querySelector('[data-eg-capture-turnstile]');
  let widgetId = null;
  let current = '';
  let waiters = [];

  const settle = (value) => {
    current = value;
    waiters.forEach((resolve) => resolve(value));
    waiters = [];
  };

  if (mount && siteKey) {
    loadTurnstile()
      .then((turnstile) => {
        widgetId = turnstile.render(mount, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: (value) => settle(value),
          'expired-callback': () => {
            current = '';
          },
          'error-callback': () => settle(''),
        });
      })
      .catch((error) => {
        log('turnstile unavailable', error.message);
        settle('');
      });
  }

  return {
    token() {
      if (current) return Promise.resolve(current);
      if (!mount || !siteKey) return Promise.resolve('');
      return new Promise((resolve) => {
        waiters.push(resolve);
        setTimeout(() => resolve(current), waitMs);
      });
    },
    reset() {
      current = '';
      if (widgetId !== null && window.turnstile) window.turnstile.reset(widgetId);
    },
  };
}

function initCaptureForm(root) {
  const form = root.querySelector('form');
  const emailInput = root.querySelector('[data-eg-capture-email]');
  const firstNameInput = root.querySelector('[data-eg-capture-first-name]');
  const phoneInput = root.querySelector('[data-eg-capture-phone]');
  const phoneErrorPanel = root.querySelector('[data-eg-capture-phone-error]');
  const consentInput = root.querySelector('[data-eg-capture-consent]');
  const honeypotInput = root.querySelector('[data-eg-capture-honeypot]');
  const submitBtn = root.querySelector('[data-eg-capture-submit]');
  const successPanel = root.querySelector('[data-eg-capture-success]');
  const errorPanel = root.querySelector('[data-eg-capture-error]');
  // Optional fourth panel (2026-09-05): content the signup UNLOCKS, rendered by
  // the caller into the snippet's `reveal` slot. When present it replaces the
  // plain success line — the podcast freebies library is a list of downloads,
  // and "you're on the list" would be the wrong words over a list she can use.
  const revealPanel = root.querySelector('[data-eg-capture-reveal]');

  if (!form || !emailInput || !submitBtn || !successPanel || !errorPanel) return;

  root.hidden = false;
  const check = root.dataset.turnstileSitekey
    ? createTurnstileCheck(root, root.dataset.turnstileSitekey)
    : null;
  const endpoint = root.dataset.captureEndpoint ?? '';
  const formId = root.dataset.formId ?? '';
  // The unlock is remembered per browser (localStorage), keyed by form id, so a
  // returning visitor finds the library open instead of being asked again for
  // an email we already have. Only an unlocking surface gets a flag.
  const unlockFlag = revealPanel && formId ? createPersistentFlag(unlockFlagKey(formId)) : null;
  let busy = false;

  if (unlockFlag && unlockFlag.isSet()) {
    form.hidden = true;
    revealPanel.hidden = false;
  }

  // A form with a consent box can only be sent once it is ticked. Synced at
  // start too, because a browser can restore a ticked box on reload.
  function syncSubmit() {
    submitBtn.disabled = busy || (consentInput ? !consentInput.checked : false);
  }
  if (consentInput) consentInput.addEventListener('change', syncSubmit);
  syncSubmit();

  function setBusy(value) {
    busy = value;
    syncSubmit();
    emailInput.disabled = value;
    if (firstNameInput) firstNameInput.disabled = value;
    if (phoneInput) phoneInput.disabled = value;
    if (consentInput) consentInput.disabled = value;
    form.setAttribute('aria-busy', String(value));
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    // Format is the browser's job (type=email + required) — checkValidity keeps
    // the native bubble as the ONLY invalid-format affordance, so the custom
    // error panel stays reserved for its one meaning: the post did not land.
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    errorPanel.hidden = true;
    if (phoneErrorPanel) phoneErrorPanel.hidden = true;
    setBusy(true);

    const turnstileToken = check ? await check.token() : '';

    const result = await submitCapture({
      url: endpoint,
      email: emailInput.value,
      firstName: firstNameInput ? firstNameInput.value : '',
      phone: phoneInput ? phoneInput.value : '',
      turnstileToken,
      honeypot: honeypotInput ? honeypotInput.value : '',
      formId,
      src: captureSrc.get() || '',
    });

    setBusy(false);
    // Spent either way: a success never submits again, a failure needs a new one.
    if (check) check.reset();

    if (result.status === CAPTURE_STATUS.SUCCESS) {
      // The success twin of capture_form_error below (Kyle, 2026-10-10): every
      // form reports both numbers, so an error count has a denominator and a
      // surface with no thank-you page still counts its signups.
      track('capture_form_success', { form_id: formId });
      form.hidden = true;
      // A shopper who just gave us their email is never chased for it again.
      exitIntentFlag.set();
      // U2 (2026-09-22): a band configured with a success url leaves the page
      // entirely instead of showing the inline thank-you. A refused/absent
      // url falls through to today's panel swap, unchanged.
      const next = safeSuccessUrl(root.dataset.successUrl);
      if (next) {
        location.assign(next);
        return;
      }
      if (revealPanel) {
        revealPanel.hidden = false;
        if (unlockFlag) unlockFlag.set();
      } else {
        successPanel.hidden = false;
      }
      return;
    }

    log('capture failed', result.reason);
    // capture_form_error (2026-09-02) — quantifies how often the fail-closed
    // retry path triggers. No email — form_id only.
    track('capture_form_error', { form_id: formId });
    if (result.code === 'invalid_phone' && phoneErrorPanel) {
      phoneErrorPanel.hidden = false;
      return;
    }
    errorPanel.hidden = false;
  });
}

/* -------------------------------------------------------------- exit-intent */

function initExitIntent(dialog) {
  if (typeof dialog.showModal !== 'function') return; // no <dialog>, no modal

  const coarsePointer =
    typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (coarsePointer) return; // R15: not relied on for mobile

  // "Not right now" — the small modal's dismissal (Kyle, 2026-07-25
  // redesign retired the (×) button). Same data attribute, same job.
  const dismiss = dialog.querySelector(SELECTORS.exitIntentClose);
  if (dismiss) dismiss.addEventListener('click', () => closeModalWithExit(dialog));

  // Clicking the scrim also dismisses (Kyle, 2026-07-25). A backdrop click
  // targets the <dialog> element itself; clicks on content target the panel
  // or its children — and the panel covers the dialog's whole box (padding
  // 0), so target === dialog can only mean the scrim.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeModalWithExit(dialog);
  });

  // Escape, routed through the same deferred close as the other two exits —
  // otherwise the UA closes instantly and this one dismissal skips the fade.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeModalWithExit(dialog);
  });

  // showModal() auto-focuses the first focusable descendant, so the modal
  // opened with the quiz link wearing a focus ring (Kyle, 2026-07-25). Land
  // the initial focus on the dialog itself instead (tabindex="-1" in the
  // snippet makes it focusable); the first real Tab reaches the link and
  // shows the ring when it is earned. Watching the `open` attribute covers
  // EVERY opener — the mouseout here and the design panel's summon button.
  if (typeof MutationObserver === 'function') {
    new MutationObserver(() => {
      if (dialog.open) dialog.focus();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  document.addEventListener('mouseout', (event) => {
    // Cursor left through the TOP of the viewport (toward the tab bar / close
    // button). relatedTarget null means it left the document, not an element.
    if (event.relatedTarget !== null || event.clientY > 0) return;
    // The modal now pitches the quiz (Kyle, 2026-07-25) — its own read of
    // the SAME persistent flag the bar reads separately (initStickyBar,
    // above); decoupled on purpose, not shared/merged logic.
    if (!shouldFireExitIntent({ flag: exitIntentFlag, coarsePointer, quizEntered: quizEnteredFlag.isSet() })) return;
    // exit_intent_shown (2026-09-02) — denominator for exit-intent's own
    // capture-vs-abandon rate.
    track('exit_intent_shown');
    exitIntentFlag.set(); // set BEFORE showing — a re-entry mid-open can't double-fire
    // Defensive: showModal throws InvalidStateError if the dialog is already
    // open. The flag above normally prevents a second call, but a caller that
    // opened the dialog another way must not turn one mouseout into a throw.
    try {
      dialog.showModal();
    } catch {
      /* already open (or not connected) — the shopper already sees it */
    }
  });
}

/* -------------------------------------------------------------------- init */

document.querySelectorAll(SELECTORS.bar).forEach(initStickyBar);
document.querySelectorAll(SELECTORS.form).forEach(initCaptureForm);
document.querySelectorAll(SELECTORS.exitIntent).forEach(initExitIntent);
